import React from 'react';

/**
 * Reusable skeleton primitives used across the app.
 * All blocks `animate-pulse` and use neutral palette so they fit any context.
 *
 * Use these — never hand-roll skeleton bars elsewhere. Keeps loading states
 * visually consistent and lets us tweak the look in one place.
 */

/** A single skeleton bar (rectangle) for inline use. */
export const SkeletonBar = ({ className = 'h-4 w-32' }) => (
  <div className={`rounded bg-neutral-200 animate-pulse ${className}`} />
);

/**
 * Header skeleton: title + subtitle + (optional) right-side buttons.
 */
export const HeaderSkeleton = ({ withActions = true }) => (
  <div className="flex items-center justify-between gap-4">
    <div className="space-y-2">
      <SkeletonBar className="h-7 w-56" />
      <SkeletonBar className="h-3 w-80 bg-neutral-100" />
    </div>
    {withActions && (
      <div className="flex gap-2">
        <SkeletonBar className="h-9 w-24 rounded-xl" />
        <SkeletonBar className="h-9 w-24 rounded-xl" />
      </div>
    )}
  </div>
);

/**
 * Metric card grid skeleton.
 * @param count number of cards (default 4)
 */
export const MetricCardsSkeleton = ({ count = 4 }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
    {Array.from({ length: count }, (_, i) => (
      <div key={i} className="soft-card p-5 space-y-3">
        <SkeletonBar className="h-8 w-8 rounded-lg" />
        <SkeletonBar className="h-3 w-24" />
        <SkeletonBar className="h-7 w-32 bg-neutral-300" />
      </div>
    ))}
  </div>
);

/**
 * Generic table skeleton — rows × columns of pulse bars.
 */
export const TableSkeleton = ({ rows = 6, columns = 5, withHeader = true }) => (
  <div className="soft-card p-5 space-y-2">
    {withHeader && <SkeletonBar className="h-4 w-32 mb-4" />}
    {Array.from({ length: rows }, (_, r) => (
      <div key={r} className="flex items-center gap-4 py-2.5">
        {Array.from({ length: columns }, (_, c) => (
          <SkeletonBar
            key={c}
            className={`h-4 ${c === columns - 1 ? 'w-16' : c === 0 ? 'w-28' : 'flex-1'} bg-neutral-100`}
          />
        ))}
      </div>
    ))}
  </div>
);

/**
 * Card grid skeleton (e.g., client cards / employee cards).
 */
export const CardGridSkeleton = ({ count = 6, columns = 3 }) => {
  const gridCols = {
    1: 'grid-cols-1',
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4',
  }[columns] || 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3';

  return (
    <div className={`grid ${gridCols} gap-4`}>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="soft-card p-5 space-y-3">
          <div className="flex items-center gap-3">
            <SkeletonBar className="h-12 w-12 rounded-full" />
            <div className="flex-1 space-y-2">
              <SkeletonBar className="h-4 w-3/4" />
              <SkeletonBar className="h-3 w-1/2 bg-neutral-100" />
            </div>
          </div>
          <SkeletonBar className="h-3 w-full bg-neutral-100" />
          <SkeletonBar className="h-3 w-5/6 bg-neutral-100" />
          <div className="flex gap-2 pt-2">
            <SkeletonBar className="h-7 w-16 rounded-md" />
            <SkeletonBar className="h-7 w-20 rounded-md" />
          </div>
        </div>
      ))}
    </div>
  );
};

/**
 * Compose a full-page skeleton: header + metrics + table.
 * Drop-in replacement for `if (loading) return <Loader2 ... />`.
 */
export const PageSkeleton = ({
  withMetrics = true,
  withTable = true,
  metricCount = 4,
  tableRows = 6,
  tableColumns = 5,
}) => (
  <div className="space-y-6" data-testid="page-skeleton">
    <HeaderSkeleton />
    {withMetrics && <MetricCardsSkeleton count={metricCount} />}
    {withTable && <TableSkeleton rows={tableRows} columns={tableColumns} />}
  </div>
);
